// package
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

//---------------------------------------------------------------------------
// custom
import { usePageLayout } from '@/app/router';
import { ROUTES } from '@/app/router/routes';
import { post75QuizScore } from '../api/quizService';
import CloseButton from '@shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

//---------------------------------------------------------------------------
const QuizAnswerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLayoutOptions } = usePageLayout();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';

  useEffect(() => {
    setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
    return () => setLayoutOptions({}); // 離開頁面時還原
  }, [setLayoutOptions]);

  const state = location.state ?? {};
  const quizContent = state.quizContent ?? '中台知多少';
  const answerResults = Array.isArray(state.answerResults) ? state.answerResults : [];
  const total =
    typeof state.total === 'number' && Number.isFinite(state.total)
      ? state.total
      : answerResults.length;

  const correctCount = useMemo(
    () => answerResults.filter((v) => v === 'correct').length,
    [answerResults],
  );

  const chapterId = state.chapterId ?? state.chapter_id;
  const [score, setScore] = useState(null);
  const [scoreMsg, setScoreMsg] = useState('');
  const [apiAnswerStats, setApiAnswerStats] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(() => chapterId != null && chapterId !== '');

  useEffect(() => {
    if (chapterId == null || chapterId === '') {
      setScoreLoading(false);
      setScore(null);
      setApiAnswerStats(null);
      return;
    }
    let cancelled = false;
    setScoreLoading(true);
    setApiAnswerStats(null);
    post75QuizScore(Number(chapterId))
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res ?? {};
        const point = data['total_point'] ?? 0;
        const totalCorrectRaw = data['total_correct'] ?? 0;
        const totalQuestionRaw = data['total_question'] ?? 0;
        const totalCorrect = Number(totalCorrectRaw);
        const totalQuestion = Number(totalQuestionRaw);
        const pointMsg = data['point_msg'] ?? '';
        setScore(typeof point === 'number' && Number.isFinite(point) ? point : null);
        setScoreMsg(pointMsg);
        setApiAnswerStats({
          correct: Number.isFinite(totalCorrect) ? totalCorrect : 0,
          total: Number.isFinite(totalQuestion) ? totalQuestion : 0,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load quiz score:', err);
        setScore(null);
        setApiAnswerStats(null);
      })
      .finally(() => {
        if (cancelled) return;
        setScoreLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chapterId]);

  const onClickHome = () => {
    navigate(ROUTES.HOME);
  };

  //---------------------------------------------------------------------------
  const headerGradientStyle = {
    background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
    boxShadow: '0px 4px 30px rgba(88.73, 187.23, 206.17, 0.50)',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  };

  const containerStyle = {
    width: '100%',
    maxWidth: 430,
    minHeight: 932,
    position: 'relative',
    background: '#F7F8FA',
    margin: '0 auto',
  };

  //---------------------------------------------------------------------------
  return (
    <section style={containerStyle}>
      {/* Header */}
      <div
        style={{
          width: '100%',
          height: 144,
          paddingTop: 33.5,
          paddingBottom: 33.5,
          left: 0,
          top: -1,
          position: 'absolute',
          ...headerGradientStyle,
        }}
      />
      
      {/* 右上角關閉/返回按鈕 */}
      <CloseButton onClick={onClickHome} />

      {/* 標題（保留原本樣式） */}
      <div
        style={{
          width: '66.5%',
          maxWidth: 286,
          left: 21,
          top: 32,
          position: 'absolute',
          justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          color: 'white',
          fontWeight: 900,
          lineHeight: isEn ? '24px' : '40px',
          wordWrap: 'break-word',
          gap: isEn ? 4 : 10,
        }}
      >
        <span style={{ fontSize: isEn ? 17 : 30 }}>{t('quiz.heroTitle')}</span>
        <span style={{ fontSize: isEn ? 15 : 28 }}>{quizContent}</span>
      </div>

      {/* 成績 */}
      <div
        style={{
          position: 'absolute',
          left: 32,
          right: 32,
          top: 315,
          height: 300,
          padding: '26px 18px',
          background: 'white',
          boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.20)',
          borderRadius: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
        }}
      >
        <div style={{ color: '#6E625B', fontSize: 20, fontWeight: 600 }}>{t('quiz-answer.correctCountLabel')}</div>
        <div
          style={{
            color: '#0097B2',
            fontSize: 44,
            fontWeight: 900,
            lineHeight: '52px',
            textAlign: 'center',
          }}
        >
          {chapterId != null && chapterId !== '' && scoreLoading
            ? t('quiz.loading')
            : chapterId != null && chapterId !== '' && apiAnswerStats != null
              ? `${apiAnswerStats.correct} / ${Math.max(apiAnswerStats.total, 0)}`
              : `${correctCount} / ${Math.max(total, 0)}`}
        </div>

        <div style={{ color: '#6E625B', fontSize: 20, fontWeight: 600, marginTop: 8 }}>
          {t('quiz-answer.pointsEarnedLabel')}
        </div>
        <div
          style={{
            color: '#4DC8E0',
            fontSize: 44,
            fontWeight: 900,
            lineHeight: '52px',
            textAlign: 'center',
          }}
        >
          {scoreLoading ? t('quiz.loading') : score != null ? `${score} ${t('quiz-answer.pointsUnit')}` : '—'}
        </div>
        <div style={{ color: '#6E625B', fontSize: 20, fontWeight: 600, marginTop: 8 }}>
          {scoreMsg}
        </div>
      </div>

      {/* 回首頁（保留原本按鈕樣式） */}
      <button
        type="button"
        onClick={onClickHome}
        aria-label={t('quiz-answer.homeAria')}
        style={{
          position: 'absolute',
          zIndex: 3,
          right: 32,
          top: 818,
          width: 80,
          height: 80,
          borderRadius: 40,
          border: 'none',
          padding: 0,
          background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
          boxShadow: '1px 1px 10px rgba(0, 0, 0, 0.50)',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRight size={80} color="#FFFFFF" strokeWidth={2.5} aria-hidden />
        </div>
      </button>
    </section>
  );
};

export default QuizAnswerPage;